import React from "react";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowLeft } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";

import store from "~/lib/store";
import { IconButton } from "~/ui/legacyFellowIndex";

import { MINI_PLAYER_OPENED_EVENT } from "../meetingRecorder/constants";

import styles from "./MiniPlayer.module.css";

const MINI_PLAYER_STORE_PREFIX_KEY = "mini-player-";
const MINI_PLAYER_POSITION_KEY = `${MINI_PLAYER_STORE_PREFIX_KEY}position}`;
const MINI_PLAYER_SIZE_KEY = `${MINI_PLAYER_STORE_PREFIX_KEY}size`;
const MINI_PLAYER_SNAP_KEY = `${MINI_PLAYER_STORE_PREFIX_KEY}snap`;
const SNAP_PADDING = 24;
const SNAP_PROXIMITY = SNAP_PADDING * 2;
const MIN_SPEED_TO_SWIPE = 3.5;
const MAX_MS_BETWEEN_MOVEMENT_AND_STOP_TO_SWIPE = 50;
const INITIAL_SIZE = {
	width: 442,
	height: 249,
};
const MIN_SIZE = {
	width: 362,
	height: 203,
};
const COLLAPSED_SIZE = {
	width: 362,
	height: 103,
};

interface MiniPlayerProps {
	title?: string;
	onClose: () => void;
	toggleMiniPlayer: () => void;
	collapsed: boolean;
}

type SnapPositions = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

function setSize(element: HTMLElement, size: { width: number; height: number }) {
	element.style.width = `${size.width}px`;
	element.style.height = `${size.height}px`;
}

function setPosition(element: HTMLElement, position: { top: string; left: string }, skipStore?: boolean) {
	element.style.top = position.top;
	element.style.left = position.left;
	if (skipStore) return;
	store.set(MINI_PLAYER_POSITION_KEY, { top: position.top, left: position.left });
}

function getSnapPosition(
	element: HTMLElement,
	swipe: { left: boolean; right: boolean; top: boolean; bottom: boolean },
): SnapPositions | null {
	const rect = element.getBoundingClientRect();
	const pageWidth = document.body.clientWidth;
	const pageHeight = document.body.clientHeight;

	const shouldSnapToTop = swipe.top || rect.top <= SNAP_PROXIMITY;
	const shouldSnapToBottom = swipe.bottom || rect.bottom >= pageHeight - SNAP_PROXIMITY;
	const shouldSnapToLeft = swipe.left || rect.left <= SNAP_PROXIMITY;
	const shouldSnapToRight = swipe.right || rect.right >= pageWidth - SNAP_PROXIMITY;

	// If none of the snap rules were satisfied we return
	if (!shouldSnapToTop && !shouldSnapToBottom && !shouldSnapToLeft && !shouldSnapToRight) return null;
	// Check which position satiesfied the snap rule and calculate the other one based on the center
	const center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
	if (shouldSnapToTop || shouldSnapToBottom) {
		let leftOrRight: "Left" | "Right" = "Left";
		if (swipe.right) {
			leftOrRight = "Right";
		} else {
			leftOrRight = swipe.left || center.x <= pageWidth / 2 ? "Left" : "Right";
		}
		return `${shouldSnapToTop ? "top" : "bottom"}${leftOrRight}`;
	}
	let topOrBottom: "top" | "bottom" = "top";
	if (swipe.bottom) {
		topOrBottom = "bottom";
	} else {
		topOrBottom = swipe.top || center.y <= pageHeight / 2 ? "top" : "bottom";
	}
	return `${topOrBottom}${shouldSnapToLeft ? "Left" : "Right"}`;
}

function snapElementIntoPosition(element: HTMLElement, snapPosition: SnapPositions) {
	const rect = element.getBoundingClientRect();
	let top = `${SNAP_PADDING}px`;
	let left = `${SNAP_PADDING}px`;
	if (snapPosition.startsWith("bottom")) {
		top = `calc(100vh - ${SNAP_PADDING + rect.height}px)`;
	}

	if (snapPosition.endsWith("Right")) {
		left = `calc(100vw - ${SNAP_PADDING + rect.width}px)`;
	}
	store.set(MINI_PLAYER_SNAP_KEY, snapPosition);
	setPosition(element, { top, left });
}

interface ResizePointsProps {
	element: HTMLElement | null;
	snappedTo: SnapPositions | null;
	onStartResize: () => void;
	onStopResize: () => void;
}

function ResizePoints({ element, snappedTo, onStartResize, onStopResize }: ResizePointsProps) {
	const onStartDrag = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>, increaseDownwads, increaseRightwards) => {
			let lastPosition = { x: e.clientX, y: e.clientY };
			onStartResize();

			function onResize(e: MouseEvent) {
				if (!element) return;
				const pageWidth = document.body.clientWidth;
				const pageHeight = document.body.clientHeight;
				// If we are going out of the page we don't want to resize
				if (
					e.y < SNAP_PADDING ||
					e.y > pageHeight - SNAP_PADDING ||
					e.x < SNAP_PADDING ||
					e.x > pageWidth - SNAP_PADDING
				)
					return;
				const rect = element.getBoundingClientRect();
				const yDifference = e.y - lastPosition.y;
				const xDifference = e.x - lastPosition.x;
				lastPosition = { x: e.clientX, y: e.clientY };
				const widthIncrease = increaseRightwards ? xDifference : -xDifference;
				const heightIncrease = increaseDownwads ? yDifference : -yDifference;
				const width = Math.max(rect.width + widthIncrease, MIN_SIZE.width);
				const height = Math.max(rect.height + heightIncrease, MIN_SIZE.height);
				const originalWidth = rect.width;
				const originalHeight = rect.height;
				setSize(element, { width, height });

				// The position is relative to the top and left
				// So if we are going upwards or leftwards we need to reposition it
				// Based on the amount of the resize
				let top = element.style.top;
				let left = element.style.left;
				// If we are change the height we need to reposition the top if we are not going downwards
				if (!increaseDownwads && originalHeight !== height) {
					const topPercent = ((rect.top - heightIncrease) * 100) / pageHeight;
					top = snappedTo ? `calc(100vh - ${SNAP_PADDING + rect.height}px)` : `${topPercent}vh`;
				}
				// If we are change the width we need to reposition the left if we are not going rightwards
				if (!increaseRightwards && originalWidth !== width) {
					const leftPercent = ((rect.left - widthIncrease) * 100) / pageWidth;
					left = snappedTo ? `calc(100vw - ${SNAP_PADDING + rect.width}px)` : `${leftPercent}vw`;
				}
				setPosition(element, { top, left });

				e.preventDefault();
				e.stopPropagation();
			}

			function onStopDrag() {
				if (!element) return;
				window.removeEventListener("mousemove", onResize);
				window.removeEventListener("mouseup", onStopDrag);
				const rect = element.getBoundingClientRect();
				const { width, height } = rect;
				store.set(MINI_PLAYER_SIZE_KEY, { width, height });
				onStopResize();
			}

			window.addEventListener("mousemove", onResize);
			window.addEventListener("mouseup", onStopDrag);
			e.preventDefault();
			e.stopPropagation();
		},
		[element, snappedTo, onStartResize, onStopResize],
	);

	const onStartDragTopLeft = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			onStartDrag(e, false, false);
		},
		[onStartDrag],
	);
	const onStartDragTopRight = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			onStartDrag(e, false, true);
		},
		[onStartDrag],
	);
	const onStartDragBottomLeft = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			onStartDrag(e, true, false);
		},
		[onStartDrag],
	);
	const onStartDragBottomRight = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			onStartDrag(e, true, true);
		},
		[onStartDrag],
	);

	// If we are snapped to a corner we only want to be able to resize in the opposite corner
	return (
		<>
			{(!snappedTo || snappedTo === "bottomRight") && (
				<div className={cx(styles.resizePoint, styles.topLeft)} onMouseDown={onStartDragTopLeft} />
			)}
			{(!snappedTo || snappedTo === "bottomLeft") && (
				<div className={cx(styles.resizePoint, styles.topRight)} onMouseDown={onStartDragTopRight} />
			)}
			{(!snappedTo || snappedTo === "topRight") && (
				<div className={cx(styles.resizePoint, styles.bottomLeft)} onMouseDown={onStartDragBottomLeft} />
			)}
			{(!snappedTo || snappedTo === "topLeft") && (
				<div className={cx(styles.resizePoint, styles.bottomRight)} onMouseDown={onStartDragBottomRight} />
			)}
		</>
	);
}

export default function MiniPlayer({
	title,
	onClose,
	children,
	toggleMiniPlayer,
	collapsed,
}: React.PropsWithChildren<MiniPlayerProps>) {
	const [dragging, setDragging] = React.useState<boolean>(false);
	const [snappedTo, setSnappedTo] = React.useState<SnapPositions | null>(store.get(MINI_PLAYER_SNAP_KEY, null));
	const miniPlayerRef = React.useRef<HTMLDivElement>(null);

	const onStartResize = React.useCallback(() => {
		setDragging(true);
	}, []);

	const onStopResize = React.useCallback(() => {
		setDragging(false);
	}, []);

	const onStartDrag = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (!miniPlayerRef.current) return;
			const originalRect = miniPlayerRef.current.getBoundingClientRect();
			// Making the click's position to be the offset while we move the mini player around
			// So while moving the mouse is still in the same position relative to the mini player
			const offset = { x: e.clientX - originalRect.left, y: e.clientY - originalRect.top };
			const startMovementTime = performance.now();
			let lastMovementX: { timestamp: number; left: boolean } = {
				timestamp: 0,
				left: true,
			};
			let lastMovementY: { timestamp: number; top: boolean } = {
				timestamp: 0,
				top: true,
			};

			function onReposition(e: MouseEvent) {
				if (!miniPlayerRef.current) return;
				const top = `${e.y - offset.y}px`;
				const left = `${e.x - offset.x}px`;
				const timestamp = performance.now();
				if (e.movementX !== 0) {
					lastMovementX = {
						timestamp,
						left: e.movementX < 0,
					};
				}
				if (e.movementY !== 0) {
					lastMovementY = {
						timestamp,
						top: e.movementY < 0,
					};
				}
				// We can use raw pxs here since we shouldn't need to care
				// about resizes while the user is dragging
				setPosition(miniPlayerRef.current, { top, left }, true);
				e.preventDefault();
				e.stopPropagation();
			}

			function onStopDrag() {
				window.removeEventListener("mousemove", onReposition);
				window.removeEventListener("mouseup", onStopDrag);
				if (!miniPlayerRef.current) return;
				setDragging(false);
				const rect = miniPlayerRef.current.getBoundingClientRect();
				// Check if a swipe was made
				const timestamp = performance.now();
				const movement = {
					x: rect.left - originalRect.left,
					y: rect.top - originalRect.top,
				};
				const timeElapsedInMs = performance.now() - startMovementTime;
				const speed = {
					x: movement.x / timeElapsedInMs,
					y: movement.y / timeElapsedInMs,
				};
				const swipe = {
					left:
						speed.x <= -MIN_SPEED_TO_SWIPE &&
						lastMovementX.left &&
						timestamp - lastMovementX.timestamp <= MAX_MS_BETWEEN_MOVEMENT_AND_STOP_TO_SWIPE,
					right:
						speed.x >= MIN_SPEED_TO_SWIPE &&
						!lastMovementX.left &&
						timestamp - lastMovementX.timestamp <= MAX_MS_BETWEEN_MOVEMENT_AND_STOP_TO_SWIPE,
					top:
						speed.y <= -MIN_SPEED_TO_SWIPE &&
						lastMovementY.top &&
						timestamp - lastMovementY.timestamp <= MAX_MS_BETWEEN_MOVEMENT_AND_STOP_TO_SWIPE,
					bottom:
						speed.y >= MIN_SPEED_TO_SWIPE &&
						!lastMovementY.top &&
						timestamp - lastMovementY.timestamp <= MAX_MS_BETWEEN_MOVEMENT_AND_STOP_TO_SWIPE,
				};
				const positionToSnap = getSnapPosition(miniPlayerRef.current, swipe);
				setSnappedTo(positionToSnap);
				// If we don't want to use snaps we just need to translate the current position
				// To accomodate for resizes
				if (!positionToSnap) {
					const pageWidth = document.body.clientWidth;
					const pageHeight = document.body.clientHeight;
					const leftPercent = (rect.left * 100) / pageWidth;
					const topPercent = (rect.top * 100) / pageHeight;
					const left = `${leftPercent}vw`;
					const top = `${topPercent}vh`;
					setPosition(miniPlayerRef.current, { top, left });
					store.set(MINI_PLAYER_SNAP_KEY, null);
					return;
				}
				snapElementIntoPosition(miniPlayerRef.current, positionToSnap);
			}
			setDragging(true);
			window.addEventListener("mousemove", onReposition);
			window.addEventListener("mouseup", onStopDrag);
		},
		[miniPlayerRef],
	);

	React.useEffect(() => {
		if (!miniPlayerRef.current) return;

		if (collapsed) {
			setSize(miniPlayerRef.current, COLLAPSED_SIZE);
		} else {
			// Setting up the initial state of the mini player based on the store
			const initialSize = store.get(MINI_PLAYER_SIZE_KEY, INITIAL_SIZE);
			// We need to make sure that the saved miniplayer's size still fits in the viewport
			// Note that this won't overwrite the saved size so if the miniplayer's size is not
			// touched and we close and open it again in a bigger viewport we will adapt to that
			const availableWidth = window.innerWidth - SNAP_PADDING * 2;
			const availableHeight = window.innerHeight - SNAP_PADDING * 2;
			const adjustedSize = {
				width: Math.min(initialSize.width, availableWidth),
				height: Math.min(initialSize.height, availableHeight),
			};
			setSize(miniPlayerRef.current, adjustedSize);
		}

		// The initial state should be what the user previously had, either snapped or not
		// And if the user never had set a state before we want to default to bottomRight
		const initialPosition = store.get(MINI_PLAYER_POSITION_KEY);
		const snappedPosition = store.get(MINI_PLAYER_SNAP_KEY, !initialPosition ? "bottomRight" : null);
		// The screen size might change since last time we snapped something into position
		// In that case we need to recalculate the position
		if (snappedPosition) {
			snapElementIntoPosition(miniPlayerRef.current, snappedPosition);
		} else {
			setPosition(miniPlayerRef.current, initialPosition);
		}
	}, [collapsed, miniPlayerRef]);

	const onAnotherMiniplayerOpened = React.useCallback(() => {
		onClose();
	}, [onClose]);

	React.useEffect(() => {
		document.dispatchEvent(new CustomEvent(MINI_PLAYER_OPENED_EVENT));
		window.addEventListener(MINI_PLAYER_OPENED_EVENT, onAnotherMiniplayerOpened);
		return () => {
			window.removeEventListener(MINI_PLAYER_OPENED_EVENT, onAnotherMiniplayerOpened);
		};
	}, [onAnotherMiniplayerOpened]);

	return (
		<div className={cx(styles.miniPlayer, { [styles.dragging]: dragging })} ref={miniPlayerRef}>
			<div className={cx(styles.header, { [styles.collapsed]: collapsed })} onMouseDown={onStartDrag}>
				<p className={styles.title}>{title ?? "Mini player"}</p>
				<div className={styles.headerActions}>
					<IconButton
						title="Back"
						icon={faArrowLeft}
						onClick={toggleMiniPlayer}
						className={cx(styles.headerButton, styles.headerExpandButton)}
						data-test-id="mini-player-expand-button"
					/>
					<IconButton
						title="Close"
						icon={faTimes}
						onClick={onClose}
						className={styles.headerButton}
						data-test-id="mini-player-close-button"
					/>
				</div>
			</div>
			{!collapsed && (
				<ResizePoints
					snappedTo={snappedTo}
					element={miniPlayerRef?.current}
					onStartResize={onStartResize}
					onStopResize={onStopResize}
				/>
			)}
			{children}
		</div>
	);
}
